import { Component, AfterViewInit, ElementRef, Renderer2, inject, PLATFORM_ID, DestroyRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavbarComponent } from './layout/navbar/navbar.component';
import { FooterComponent } from './layout/footer/footer.component';
import { TickerComponent } from './layout/ticker/ticker.component';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NavbarComponent,
    FooterComponent,
    TickerComponent
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class App implements AfterViewInit {
  title = 'TriStar Finance';
  isAdminRoute = false;
  private observer: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  private router = inject(Router);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initScrollReveal();
      this.watchRouteChanges();
      this.watchDOMChanges();
      this.setupCleanup();
    }
    // Check initial route
    this.checkIfAdminRoute(this.router.url);
  }

  private setupCleanup() {
    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
      this.mutationObserver?.disconnect();
    });
  }

  private initScrollReveal() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.renderer.addClass(entry.target, 'vis');
          this.observer?.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    this.observeElements();
  }

  private observeElements() {
    const revealElements = this.el.nativeElement.querySelectorAll('.rv:not(.vis)');
    revealElements.forEach((el: Element) => {
      this.observer?.observe(el);
    });
  }

  private watchRouteChanges() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        window.scrollTo(0, 0);
        this.checkIfAdminRoute(event.url);
        setTimeout(() => {
          this.observeElements();
        }, 150);
      });
  }

  private watchDOMChanges() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          this.observeElements();
        }
      });
    });

    const config = { childList: true, subtree: true };
    this.mutationObserver.observe(this.el.nativeElement, config);
  }

  private checkIfAdminRoute(url: string) {
    this.isAdminRoute = url.startsWith('/admin');
  }
}
